import React, { useState } from "react";
import ChecklistCard from "../../ChecklistCard.js";
import "../../../styles/ResidentialListing.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import importantNotes from "../../../data/Manitoba/importantNotes.js";
import ImportantNotes from "../../ImportantNotes.js";
import BackToTop from "../../BackToTop.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

export default function ResidentialListing() {
  const [ListingData, setListingData] = useState([
    "Agency Disclosure (Working With a RE Salesperson)",
    "MLS Real Estate Listing Contract",
    "Service Agreement - FORM E",
    "Limited Joint Representation (Best Practice)",
    "Title Search (Best Practice)",
    "Related FINTRAC Documents",
    "Full MLS Sheet",
  ]);

  const [ListingType, setListingType] = useState("MLS");
  const [IsDisclosure, setIsDisclosure] = useState("No");
  const [Is244, setIs244] = useState("No");

  // State for conditional documents that can be added/removed
  const [conditionalDocuments, setConditionalDocuments] = useState({
    "Is the listing going live on MLS later than 2 business days after signing?": false,
    "Do you or close relatives own the property?": false,
    "Do you have a delayed offer presentation date or showing directions?": false,
    "Is a Property Disclosure Statement being completed?": false,
    "Is this an estate sale?": false,
    "Is there a Power of Attorney for the Seller?": false,
  });

  // Mapping of checkbox labels to their corresponding listing data strings
  const documentMapping = {
    "Is the listing going live on MLS later than 2 business days after signing?":
      "Marketing Release Date - Multiple Listing Schedule",
    "Do you or close relatives own the property?": "Written Disclosure Form",
    "Do you have a delayed offer presentation date or showing directions?":
      "Direction of Seller Regarding Showings/Offers",
    "Is a Property Disclosure Statement being completed?":
      "Property Disclosure Statement (PDS)",
    "Is this an estate sale?": "Executor/Executrix Form from Lawyer",
    "Is there a Power of Attorney for the Seller?":
      "Power of Attorney Form from Lawyer",
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#506CAA",
        dark: "#506CAA",
      },
      text: {
        primary: "#31303F",
        secondary: "#506CAA",
      },
    },
  });

  const setExclusive = (e) => {
    setListingType("Exclusive");
    let newListingData = ListingData;
    if (
      newListingData.includes("REALTOR Cooperation Disclosure and Consent") ===
      true
    ) {
      return;
    } else {
      newListingData.push("REALTOR Cooperation Disclosure and Consent");
      newListingData[1] = "MREA Exclusive Listing Contract";
    }
    setListingData(newListingData.filter((item) => item !== "Full MLS Sheet"));
  };

  const setMLS = (e) => {
    setListingType("MLS");
    let newListingData = ListingData;
    if (
      newListingData.includes("REALTOR Cooperation Disclosure and Consent") ===
      false
    ) {
      return;
    } else {
      newListingData.push("Full MLS Sheet");
      newListingData[1] = "MLS Real Estate Listing Contract";
    }
    setListingData(
      newListingData.filter(
        (item) => item !== "REALTOR Cooperation Disclosure and Consent",
      ),
    );
  };

  // Function to handle conditional document checkboxes
  const toggleConditionalDocument = (documentName) => {
    setConditionalDocuments((prev) => {
      const newState = {
        ...prev,
        [documentName]: !prev[documentName],
      };

      // Get the corresponding string for the listing data
      const listingDataString = documentMapping[documentName];

      // Update Is244 state if this is the delayed offer presentation checkbox
      if (
        documentName ===
        "Do you have a delayed offer presentation date or other directions?"
      ) {
        setIs244(newState[documentName] ? "Yes" : "No");
      }
      if (documentName === "Do you or close relatives own the property?") {
        setIsDisclosure(newState[documentName] ? "Yes" : "No");
      }

      // Update the listing data based on checkbox state
      setListingData((prevListingData) => {
        let newListingData = [...prevListingData];

        if (newState[documentName]) {
          // Add document if checked and not already present
          if (!newListingData.includes(listingDataString)) {
            newListingData.push(listingDataString);
          }
        } else {
          // Remove document if unchecked
          newListingData = newListingData.filter(
            (item) => item !== listingDataString,
          );
        }

        return newListingData;
      });

      return newState;
    });
  };

  return (
    <div className="checklists w-full flex flex-col items-center justify-center">
      <div className="flex flex-col w-full pl-4 ">
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 ">MLS or Exclusive Listing?</div>
          <ThemeProvider theme={theme}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue="MLS"
              >
                <FormControlLabel
                  value="MLS"
                  control={<Radio size="small" onClick={setMLS} />}
                  label="MLS"
                />
                <FormControlLabel
                  value="Exclusive"
                  control={<Radio size="small" onClick={setExclusive} />}
                  label="Exclusive"
                />
              </RadioGroup>
            </FormControl>
          </ThemeProvider>
        </div>

        {/* Check all that apply section */}
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 pb-2 ">
            For each "Yes" response, check the box that applies:
          </div>
          <ThemeProvider theme={theme}>
            <div className="flex flex-col ">
              {Object.keys(conditionalDocuments).map((documentName) => (
                <FormControlLabel
                  key={documentName}
                  control={
                    <Checkbox
                      size="small"
                      checked={conditionalDocuments[documentName]}
                      onChange={() => toggleConditionalDocument(documentName)}
                    />
                  }
                  label={documentName}
                />
              ))}
            </div>
          </ThemeProvider>
        </div>

        <div className="paragraph">
          <p>
            <strong>Seller Net Sheet: </strong>A seller net sheet gives
            homeowners a general estimate of their proceeds after expenses,
            while helping listing agents identify potential issues early and
            determine whether the property is saleable based on the seller's
            expectations and the property's financials.
            <br />
            <a
              href="https://drive.google.com/drive/folders/1Nrze9PIqWi_OQQZ8dEdI8nP4j9inRiJD"
              rel="noopener noreferrer"
              target="_blank"
            >
              Download the Seller Net Sheet
            </a>
            <br />
            <br />
          </p>
        </div>
      </div>
      {IsDisclosure === "Yes" ? (
        <>
          <div className="flex w-full pl-4 pt-3 items-center ">
            <div className="pb-5 pr-3">
              <div className="float-left mr-3 pt-2">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  size="2xl"
                  color="#cc0000"
                />
              </div>
              <div>
                CONFIRM STATEMENT , you cannot represent both clients in a trade
                when you have interest in the listed property. As a result, if
                you bring a Buyer client to the trade, you must release or refer
                one of the client(s) to another agent. Additionally, you can
                collect a referral fee on the client referral.
              </div>
            </div>
          </div>
        </>
      ) : (
        ""
      )}
      <ChecklistCard
        province={"MB"}
        Title1={""}
        CheckListData={ListingData}
        Header={ListingType + " Listing:"}
        Header2={"Seller Client"}
      />

      <div className="important-notes flex flex-col w-full justify-left pt-6">
        <strong>Important Notes:</strong>
        <div className="pb-3"></div>
        {Is244 === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("directions") === true,
            )}
          />
        ) : (
          ""
        )}
        {IsDisclosure === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("disclosure") === true,
            )}
          />
        ) : (
          ""
        )}
        {ListingType === "Exclusive" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("exclusiveListing") === true,
            )}
          />
        ) : (
          ""
        )}
        <ImportantNotes
          Notes={importantNotes.filter(
            (notes) => notes.category.includes("listing") === true,
          )}
        />
        {ListingType === "MLS" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("mls") === true,
            )}
          />
        ) : (
          ""
        )}
      </div>
      <BackToTop />
    </div>
  );
}
